/**
 * navigation.ts — Mathematical calculations for navigation aids.
 *
 * Provides pure screen-edge projection math for HUD indicators (such as the
 * directional arrow pointing home after binning the poop).
 */
import { WORLD } from '../config/constants';
import type { Vec2 } from '../utils/vec2';

export interface EdgeArrowState {
  /** X coordinate on screen (in pixels). */
  x: number;
  /** Y coordinate on screen (in pixels). */
  y: number;
  /** Rotation angle in degrees (0 = right, 90 = down, -90 = up, 180 = left). */
  angleDeg: number;
  /**
   * How far to turn a **north-pointing** glyph so it aims at the target, in
   * degrees clockwise — i.e. what to hand CSS `rotate()`.
   *
   * This is not `angleDeg`, and the difference is the whole reason it's a
   * field rather than something the HUD works out. `angleDeg` measures from
   * screen-*right*, because that's where `atan2` starts; the `▲` in the marker
   * rests pointing screen-*up*. The quarter turn between those two zeroes is
   * the `+ 90`, and getting its sign wrong is invisible in a code review and
   * unmistakable in play — the marker sits on the edge of the screen nearest
   * home with its arrow aimed back at the walker.
   */
  rotationDeg: number;
  /** Distance in world units from player to target. */
  distance: number;
}

/**
 * Compute edge-pinned screen coordinates and rotation angle for pointing from
 * the player toward a target location in world space.
 *
 * @param playerPos Gameplay 2D position of the player (world units)
 * @param targetPos Gameplay 2D position of the target (world units)
 * @param viewportWidth Screen width in pixels
 * @param viewportHeight Screen height in pixels
 * @param margin Margin in pixels from screen edge
 */
export function computeEdgeArrow(
  playerPos: Vec2,
  targetPos: Vec2,
  viewportWidth: number,
  viewportHeight: number,
  margin = 36,
): EdgeArrowState {
  const dx = targetPos.x - playerPos.x;
  const dy = targetPos.y - playerPos.y;
  const distance = Math.hypot(dx, dy);

  // Gameplay 2D angle: +x is screen-right, +y is screen-down
  const angleRad = Math.atan2(dy, dx);
  const angleDeg = (angleRad * 180) / Math.PI;

  const cx = viewportWidth / 2;
  const cy = viewportHeight / 2;

  const minX = margin;
  const maxX = viewportWidth - margin;
  const minY = margin;
  const maxY = viewportHeight - margin;

  const dirX = Math.cos(angleRad);
  const dirY = Math.sin(angleRad);

  let tX = Infinity;
  let tY = Infinity;

  if (dirX > 1e-5) {
    tX = (maxX - cx) / dirX;
  } else if (dirX < -1e-5) {
    tX = (minX - cx) / dirX;
  }

  if (dirY > 1e-5) {
    tY = (maxY - cy) / dirY;
  } else if (dirY < -1e-5) {
    tY = (minY - cy) / dirY;
  }

  const t = Math.min(tX, tY);
  const posX = Math.max(minX, Math.min(maxX, cx + dirX * t));
  const posY = Math.max(minY, Math.min(maxY, cy + dirY * t));

  return {
    x: posX,
    y: posY,
    angleDeg,
    rotationDeg: angleDeg + 90,
    distance,
  };
}

/**
 * Label a world-unit distance for the player, in metres.
 *
 * Distances travel through the game as world units and are only ever a metre
 * count at the moment they're written on screen — `WORLD.UNITS_PER_METRE` is
 * the conversion, and this is the one place that applies it. Printing the unit
 * count with an `m` after it is what made a walk across the street read as
 * 160m.
 *
 * Under 10m it keeps one decimal: the last stretch to the doorstep is a few
 * metres long, and whole metres there leave the readout sitting on the same
 * number while the walker is visibly still moving.
 */
export function formatDistance(units: number): string {
  const metres = units / WORLD.UNITS_PER_METRE;
  return metres < 10 ? `${metres.toFixed(1)}m` : `${Math.round(metres)}m`;
}
